/**
 * How a warning's times read, the same in both apps: the `formatTime` the view
 * models take, and the day labels under the list's timeline.
 *
 * Relative words ("today at 23:59", "tomorrow at 00:00") in most locales, in
 * the PLACE's timezone (a warning is about a place, not the phone). Polish,
 * Russian, Ukrainian, Croatian and Serbian always get an absolute date: their
 * "until"/"from" govern a case a "{time}" placeholder cannot inflect ("до
 * завтра" vs "завтра"), so the status lines read "до 4 окт., 20:59" there.
 *
 * Imports `../Dates`, never the utils barrel (`importCycles.test.ts`).
 */
import {messages} from '@smartmixin/i18n';

import * as Dates from '../Dates';
import type {WarningsIntl} from './view';

/** Locales whose status lines show a date, never "today" / "tomorrow". */
export const ABSOLUTE_DATE_LOCALES = ['pl', 'ru', 'uk', 'hr', 'sr'] as const;

const language = (locale: string) =>
  locale.trim().toLowerCase().split(/[-_]/)[0];

const isAbsolute = (locale: string) =>
  (ABSOLUTE_DATE_LOCALES as readonly string[]).includes(language(locale));

/** moment's name for the locale (`sr` is registered as `sr-cyrl`). */
const momentLocale = (locale: string) =>
  language(locale) === 'sr' ? 'sr-cyrl' : language(locale);

export type WarningTimeOptions = {
  intl: WarningsIntl;
  /** The reader's locale, e.g. "en", "de", "pl". */
  locale: string;
  /** The place's IANA timezone, e.g. "Europe/Madrid". */
  timezone: string;
  /** Epoch seconds: what "today" is relative to. */
  now: number;
};

const local = (epochSeconds: number, {locale, timezone}: WarningTimeOptions) =>
  Dates.timestampToMoment(epochSeconds, timezone).locale(momentLocale(locale));

/**
 * The locale's day-and-month without the year, from its long date format:
 * "MMM D" (en), "D. MMM" (de), "D [de] MMM" (es), "M月D日" (ja), "MMM D."
 * (hu). Only the year, the words glued to it and the separators before it
 * are dropped; the month is abbreviated.
 */
const dayMonthFormat = (m: ReturnType<typeof local>): string =>
  m
    .localeData()
    .longDateFormat('LL')
    .replace(
      /[\s,.]*(?:\[[^\]]*\]\s*)?YYYY(?:[年년.]|\s*\[?г\.?\]?)?[\s,]*/,
      ' ',
    )
    .trim()
    .replace('MMMM', 'MMM');

/** Whole calendar days from `now` to the instant, in the place's timezone. */
const dayOffset = (epochSeconds: number, options: WarningTimeOptions) => {
  const day = local(epochSeconds, options).startOf('day');
  const today = local(options.now, options).startOf('day');
  // Rounded: a DST change makes one day 23 or 25 hours long.
  return Math.round(day.diff(today, 'hours') / 24);
};

/**
 * Builds the `formatTime` for `summaryViewModel` / `listViewModel` /
 * `detailViewModel`: "today at 23:59", "tomorrow at 8:59 PM", "Tue 14:00",
 * "Oct 12 14:00" (the locale's day-month order); in pl/ru/uk/hr/sr always
 * "4 paź, 20:59".
 */
export const warningTimeFormatter =
  (options: WarningTimeOptions & {is24Hour: boolean}) =>
  (epochSeconds: number): string => {
    const {intl, locale, is24Hour} = options;
    const m = local(epochSeconds, options);
    const time = m.format(is24Hour ? 'HH:mm' : 'h:mm A');
    if (isAbsolute(locale)) {
      // Croatian and Serbian write the day as an ordinal: "4. lis.".
      const day = ['hr', 'sr'].includes(language(locale)) ? 'D.' : 'D';
      return `${m.format(`${day} MMM`)}, ${time}`;
    }
    const offset = dayOffset(epochSeconds, options);
    if (offset === 0) {
      return intl.formatMessage(messages.weatherWarnings.todayAt, {time});
    }
    if (offset === 1) {
      return intl.formatMessage(messages.weatherWarnings.tomorrowAt, {time});
    }
    if (offset > 1 && offset < 7) {
      return `${m.format('ddd')} ${time}`;
    }
    return `${m.format(dayMonthFormat(m))} ${time}`;
  };

/**
 * The label under a midnight tick of the list's timeline: "Today",
 * "Tomorrow", then the weekday ("Mon").
 */
export const laneDayLabel = (
  epochSeconds: number,
  options: WarningTimeOptions,
): string => {
  const offset = dayOffset(epochSeconds, options);
  if (offset === 0) {
    return options.intl.formatMessage(messages.calendar.today);
  }
  if (offset === 1) {
    return options.intl.formatMessage(messages.weatherWarnings.tomorrow);
  }
  return local(epochSeconds, options).format('ddd');
};

/**
 * Builds the period rows' and the detail window's visual range: "Now – 11:59
 * PM", "Tomorrow 12:00 AM – 8:59 PM", "Now – Tomorrow 11:59 PM". The start
 * reads "Now" while in force, else its day label and clock; the end carries a
 * day only when it falls on another day than the start (than today, while in
 * force). An open end reads "Now" in force and "From …" before. Day labels are
 * `laneDayLabel`'s nominative words, so the absolute-date locales need no
 * special case here: nothing governs them.
 *
 * The read-aloud twin is the list view model's slice `accessibleName`.
 */
export const warningRangeFormatter =
  (options: WarningTimeOptions & {is24Hour: boolean}) =>
  (start: number, end: number | null, active: boolean): string => {
    const {intl, is24Hour} = options;
    const clock = (epochSeconds: number) =>
      local(epochSeconds, options).format(is24Hour ? 'HH:mm' : 'h:mm A');
    const day = (epochSeconds: number) =>
      `${laneDayLabel(epochSeconds, options)} ${clock(epochSeconds)}`;
    const from = active ? intl.formatMessage(messages.common.now) : day(start);
    if (end === null) {
      return active
        ? from
        : intl.formatMessage(messages.weatherWarnings.fromTime, {time: from});
    }
    const sameDay =
      dayOffset(end, options) === (active ? 0 : dayOffset(start, options));
    return `${from} – ${sameDay ? clock(end) : day(end)}`;
  };
